import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { pushApi, type BrowserPushSubscriptionPayload } from '@/api/push'
import { getErrorMessage } from '@/utils/error'

export type WebPushStatus =
  | 'unsupported'
  | 'loading'
  | 'disabled'
  | 'enabled'
  | 'denied'
  | 'error'

function urlBase64ToArrayBuffer(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const bytes = Uint8Array.from([...raw].map((character) => character.charCodeAt(0)))
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

function toPayload(subscription: PushSubscription): BrowserPushSubscriptionPayload | null {
  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return null
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    content_encoding: 'aes128gcm',
  }
}

async function getBrowserSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.register('/sw.js')
  return registration.pushManager.getSubscription()
}

export async function detachWebPushBeforeLogout(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  try {
    const browserSubscription = await getBrowserSubscription()
    if (!browserSubscription) return
    const backendSubscriptions = await pushApi.listSubscriptions()
    const backendSubscription = backendSubscriptions.find(
      (item) => item.endpoint === browserSubscription.endpoint,
    )
    if (backendSubscription) await pushApi.unsubscribe(backendSubscription.id)
  } catch {
    // Logout must continue even when the browser or API is unavailable.
  }
}

export function useWebPush() {
  const { t } = useTranslation()
  const supported = 'serviceWorker' in navigator && 'PushManager' in window
  const [status, setStatus] = useState<WebPushStatus>(supported ? 'loading' : 'unsupported')
  const [error, setError] = useState<string | null>(null)

  const resolveError = useCallback(
    (caught: unknown, fallbackKey: string) =>
      getErrorMessage(caught) || t(fallbackKey),
    [t],
  )

  const refresh = useCallback(async () => {
    if (!supported) return
    setStatus('loading')
    setError(null)
    try {
      if (Notification.permission === 'denied') {
        setStatus('denied')
        return
      }
      const browserSubscription = await getBrowserSubscription()
      if (!browserSubscription) {
        setStatus('disabled')
        return
      }
      const backendSubscriptions = await pushApi.listSubscriptions()
      const linked = backendSubscriptions.some(
        (item) => item.endpoint === browserSubscription.endpoint,
      )
      if (!linked) {
        const payload = toPayload(browserSubscription)
        if (!payload) {
          setError(t('notifications.push.invalidSubscription'))
          setStatus('error')
          return
        }
        await pushApi.subscribe(payload)
      }
      setStatus('enabled')
    } catch (caught) {
      setError(resolveError(caught, 'notifications.push.actionError'))
      setStatus('error')
    }
  }, [resolveError, supported, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const enable = useCallback(async () => {
    if (!supported) return
    setStatus('loading')
    setError(null)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'disabled')
        return
      }
      const registration = await navigator.serviceWorker.register('/sw.js')
      const vapid = await pushApi.getVapidPublicKey()
      if (!vapid.enabled || vapid.publicKey.trim() === '') {
        setError(t('notifications.push.notConfigured'))
        setStatus('error')
        return
      }
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToArrayBuffer(vapid.publicKey),
        }))
      const payload = toPayload(subscription)
      if (!payload) {
        setError(t('notifications.push.invalidSubscription'))
        setStatus('error')
        return
      }
      await pushApi.subscribe(payload)
      setStatus('enabled')
    } catch (caught) {
      setError(resolveError(caught, 'notifications.push.actionError'))
      setStatus('error')
    }
  }, [resolveError, supported, t])

  const disable = useCallback(async () => {
    if (!supported) return
    setStatus('loading')
    setError(null)
    try {
      const browserSubscription = await getBrowserSubscription()
      if (browserSubscription) {
        const backendSubscriptions = await pushApi.listSubscriptions()
        const backendSubscription = backendSubscriptions.find(
          (item) => item.endpoint === browserSubscription.endpoint,
        )
        if (backendSubscription) await pushApi.unsubscribe(backendSubscription.id)
        await browserSubscription.unsubscribe()
      }
      setStatus('disabled')
    } catch (caught) {
      setError(resolveError(caught, 'notifications.push.actionError'))
      setStatus('error')
    }
  }, [resolveError, supported])

  return { status, error, enable, disable, refresh }
}
